/**
 * @file SuperAdminDashboard.js
 * @description Page component for Super Admin operations, multi-tenant provisioning, subscription management, and system audit logs.
 */

import React, { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import {
  getTenants,
  createTenant,
  updateTenantStatus,
  updateTenantPlan,
  getAuditLogs,
} from "../api/services";
import toast from "react-hot-toast";

/**
 * Main SuperAdminDashboard page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function SuperAdminDashboard() {
  const [tenants, setTenants] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tenants"); // tenants | audit

  // Create Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({
    companyName: "",
    ownerName: "",
    email: "",
    password: "",
    phone: "",
    subscriptionPlan: "Pro",
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === "tenants") {
        const res = await getTenants();
        if (res.data?.success) setTenants(res.data.data);
      } else {
        const res = await getAuditLogs({ limit: 50 });
        if (res.data?.success) setAuditLogs(res.data.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load super admin data");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await createTenant(form);
      if (res.data?.success) {
        toast.success("✅ Tenant created successfully!");
        setShowCreateModal(false);
        setForm({
          companyName: "",
          ownerName: "",
          email: "",
          password: "",
          phone: "",
          subscriptionPlan: "Pro",
        });
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create tenant");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (tenant) => {
    const newStatus = tenant.status === "Active" ? "Suspended" : "Active";
    const toastId = toast.loading(`Updating ${tenant.companyName}...`);
    try {
      const res = await updateTenantStatus(tenant.id, newStatus);
      if (res.data?.success) {
        toast.success(`Tenant ${newStatus.toLowerCase()} successfully!`, { id: toastId });
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status", { id: toastId });
    }
  };

  const handlePlanChange = async (tenantId, newPlan) => {
    try {
      const res = await updateTenantPlan(tenantId, { subscriptionPlan: newPlan });
      if (res.data?.success) {
        toast.success("Plan updated successfully!");
        fetchData();
      }
    } catch (err) {
      toast.error("Failed to update subscription plan");
    }
  };

  return (
    <Layout title="SaaS Super Admin Platform Portal">
      <div style={{ padding: "20px", fontFamily: "sans-serif" }}>
        {/* Header Banner */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
            color: "white",
            padding: "24px",
            borderRadius: "10px",
            marginBottom: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "700" }}>
              👑 SaaS Super Admin Management
            </h1>
            <p style={{ margin: "6px 0 0", color: "#94a3b8", fontSize: "14px" }}>
              Manage multi-tenant organizations, subscription plans, platform status, and audit logs.
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              background: "#10b981",
              color: "white",
              border: "none",
              padding: "10px 18px",
              borderRadius: "6px",
              fontWeight: "600",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            + Create New Tenant
          </button>
        </div>

        {/* Tab Selector */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
          <button
            onClick={() => setActiveTab("tenants")}
            style={{
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              cursor: "pointer",
              background: activeTab === "tenants" ? "#2563eb" : "#e2e8f0",
              color: activeTab === "tenants" ? "white" : "#475569",
            }}
          >
            🏢 Organizations / Tenants ({tenants.length})
          </button>
          <button
            onClick={() => setActiveTab("audit")}
            style={{
              padding: "10px 20px",
              borderRadius: "6px",
              border: "none",
              fontWeight: "600",
              cursor: "pointer",
              background: activeTab === "audit" ? "#2563eb" : "#e2e8f0",
              color: activeTab === "audit" ? "white" : "#475569",
            }}
          >
            🛡️ Platform Audit Logs
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
            Loading platform data...
          </div>
        ) : activeTab === "tenants" ? (
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#475569" }}>
                  <th style={{ padding: "12px 16px" }}>Company / Tenant</th>
                  <th style={{ padding: "12px 16px" }}>Owner & Email</th>
                  <th style={{ padding: "12px 16px" }}>Plan</th>
                  <th style={{ padding: "12px 16px" }}>Users</th>
                  <th style={{ padding: "12px 16px" }}>Customers</th>
                  <th style={{ padding: "12px 16px" }}>Cheques</th>
                  <th style={{ padding: "12px 16px" }}>Status</th>
                  <th style={{ padding: "12px 16px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "12px 16px", fontWeight: "600" }}>
                      {t.companyName}
                      <br />
                      <span style={{ fontSize: "11px", color: "#94a3b8", fontFamily: "monospace" }}>
                        ID: {t.id}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      {t.ownerName}
                      <br />
                      <span style={{ fontSize: "12px", color: "#64748b" }}>{t.email}</span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <select
                        value={t.subscriptionPlan}
                        onChange={(e) => handlePlanChange(t.id, e.target.value)}
                        style={{ padding: "4px 8px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                      >
                        <option value="Trial">Trial</option>
                        <option value="Pro">Pro</option>
                        <option value="Enterprise">Enterprise</option>
                      </select>
                    </td>
                    <td style={{ padding: "12px 16px" }}>{t._count?.users || 0}</td>
                    <td style={{ padding: "12px 16px" }}>{t._count?.customers || 0}</td>
                    <td style={{ padding: "12px 16px" }}>{t._count?.cheques || 0}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <span
                        style={{
                          padding: "4px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                          background: t.status === "Active" ? "#dcfce7" : "#fee2e2",
                          color: t.status === "Active" ? "#166534" : "#991b1b",
                        }}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <button
                        onClick={() => handleToggleStatus(t)}
                        style={{
                          padding: "6px 12px",
                          borderRadius: "4px",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: "600",
                          fontSize: "12px",
                          background: t.status === "Active" ? "#ef4444" : "#22c55e",
                          color: "white",
                        }}
                      >
                        {t.status === "Active" ? "Suspend" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <th style={{ padding: "12px 16px" }}>Timestamp</th>
                  <th style={{ padding: "12px 16px" }}>Tenant</th>
                  <th style={{ padding: "12px 16px" }}>User</th>
                  <th style={{ padding: "12px 16px" }}>Action</th>
                  <th style={{ padding: "12px 16px" }}>Resource</th>
                  <th style={{ padding: "12px 16px" }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: "1px solid #f1f5f9", fontSize: "13px" }}>
                    <td style={{ padding: "10px 16px", color: "#64748b" }}>
                      {new Date(log.createdAt).toLocaleString()}
                    </td>
                    <td style={{ padding: "10px 16px", fontFamily: "monospace" }}>
                      {log.tenantId || "Global"}
                    </td>
                    <td style={{ padding: "10px 16px" }}>{log.userName || "System"}</td>
                    <td style={{ padding: "10px 16px", fontWeight: "600" }}>{log.action}</td>
                    <td style={{ padding: "10px 16px" }}>{log.resource}</td>
                    <td style={{ padding: "10px 16px", color: "#475569" }}>{log.details || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Create Tenant Modal */}
        {showCreateModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "white",
                padding: "24px",
                borderRadius: "10px",
                width: "480px",
                maxWidth: "90vw",
              }}
            >
              <h2 style={{ margin: "0 0 16px", fontSize: "20px" }}>Create New SaaS Tenant</h2>
              <form onSubmit={handleCreateTenant} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px" }}>
                    Company Name *
                  </label>
                  <input
                    required
                    type="text"
                    value={form.companyName}
                    onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                    style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                    placeholder="e.g. ABC Financial Services"
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px" }}>
                    Owner Name *
                  </label>
                  <input
                    required
                    type="text"
                    value={form.ownerName}
                    onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                    style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                    placeholder="e.g. John Doe"
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px" }}>
                    Admin Email *
                  </label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                    placeholder="admin@abcfinancial.com"
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px" }}>
                    Admin Password *
                  </label>
                  <input
                    required
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px" }}>
                    Subscription Plan
                  </label>
                  <select
                    value={form.subscriptionPlan}
                    onChange={(e) => setForm({ ...form, subscriptionPlan: e.target.value })}
                    style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #cbd5e1" }}
                  >
                    <option value="Trial">Trial</option>
                    <option value="Pro">Pro</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    style={{ padding: "8px 16px", borderRadius: "4px", border: "1px solid #cbd5e1", background: "none" }}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={submitting}
                    type="submit"
                    style={{ padding: "8px 16px", borderRadius: "4px", border: "none", background: "#2563eb", color: "white", fontWeight: "600" }}
                  >
                    {submitting ? "Creating..." : "Create Tenant"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
